import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, BookOpen } from "lucide-react";

import { fetchIssues, fetchEditions } from "../api/issues";
import { Spinner } from "../components/Spinner";
import { shortDate } from "../lib/format";

function fmtMB(b: number): string {
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

export function Edition() {
  const params = useParams<{ channelId: string; editionId: string }>();
  const channelId = Number(params.channelId);
  const editionId = Number(params.editionId);

  // Load both: edition list (to find OUR edition's metadata) + the issues.
  const eds = useQuery({
    queryKey: ["editions", channelId],
    queryFn: () => fetchEditions(channelId),
    enabled: !!channelId,
  });
  const issues = useQuery({
    queryKey: ["issues", editionId],
    queryFn: () => fetchIssues(editionId, { limit: 90 }),
    enabled: !!editionId,
  });

  const edition = eds.data?.find((e) => e.edition_id === editionId);

  return (
    <div className="flex flex-col gap-8 animate-fade-up">
      <Link
        to={`/browse/${channelId}`}
        className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-muted)] hover:text-[var(--fg)] inline-flex items-center gap-1.5"
      >
        <ArrowLeft size={12} /> {edition?.channel_name ?? "Back"}
      </Link>

      <header className="rule-double pt-6">
        <div className="flex items-baseline gap-3 flex-wrap">
          <span className="np-label">Section III · Issues</span>
          {edition?.is_crawlable === false && (
            <span className="np-chip">paused</span>
          )}
        </div>
        <h1 className="font-display font-extrabold tracking-tight leading-[0.94] mt-2"
            style={{ fontSize: "clamp(40px, 5vw, 64px)" }}>
          {edition?.city ?? "—"}
        </h1>
        <p className="font-display italic text-[18px] text-[var(--fg-muted)] mt-2">
          {edition?.channel_name}
          {edition?.state ? ` · ${edition.state}` : ""}
          {edition?.language ? ` · ${edition.language}` : ""}
        </p>
      </header>

      {issues.isLoading && (
        <div className="py-12 flex justify-center"><Spinner label="Loading issues" /></div>
      )}

      {issues.data && issues.data.length === 0 && (
        <div className="np-card-inset p-8 text-center">
          <BookOpen className="mx-auto mb-3 text-[var(--fg-faint)]" />
          <div className="font-display text-xl mb-1">No issues yet</div>
          <div className="font-sans text-[14px] text-[var(--fg-muted)]">
            The crawler hasn't captured an edition for this city.
          </div>
        </div>
      )}

      {issues.data && issues.data.length > 0 && (
        <section className="np-card overflow-hidden">
          <div className="px-5 py-3 border-b grid grid-cols-12 gap-3 font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)]"
               style={{ borderColor: "var(--rule)" }}>
            <div className="col-span-3">Date</div>
            <div className="col-span-2 text-right">Pages</div>
            <div className="col-span-2 text-right">Size</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-3 text-right">Open</div>
          </div>
          <ul>
            {issues.data.map((i, idx) => (
              <li
                key={i.issue_id}
                className="border-b transition-colors hover:bg-[var(--bg-inset)]"
                style={{ borderColor: "var(--rule)" }}
              >
                <Link
                  to={`/reader/${i.issue_id}`}
                  className="grid grid-cols-12 gap-3 items-baseline px-5 py-3.5"
                >
                  <div className="col-span-3 flex items-baseline gap-3">
                    <span className="font-mono text-[10px] text-[var(--fg-faint)] tabular-nums w-5">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <span className="font-display text-[18px] font-medium">
                      {shortDate(i.business_date)}
                    </span>
                  </div>
                  <div className="col-span-2 text-right font-mono tabular-nums">
                    {i.page_count}
                  </div>
                  <div className="col-span-2 text-right font-mono text-[var(--fg-muted)] tabular-nums">
                    {fmtMB(i.total_bytes)}
                  </div>
                  <div className="col-span-2">
                    <StatusBadge status={i.status} />
                  </div>
                  <div className="col-span-3 text-right font-mono text-[11px] uppercase tracking-folio text-[var(--primary)]">
                    Read →
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const s = status.toLowerCase();
  const color =
    s === "ok" ? "var(--primary)" :
    s === "failed" ? "var(--accent)" :
    "var(--fg-muted)";
  return (
    <span className="font-mono text-[10px] uppercase tracking-folio inline-flex items-center gap-1.5">
      <span
        className="w-1.5 h-1.5 inline-block"
        style={{ background: color }}
      />
      <span style={{ color }}>{status}</span>
    </span>
  );
}
